// client/src/utils/musicMatch.js
//
// Client-side music matching for Coach Mode cards. Lives next to the
// server matcher in spirit but runs against an in-memory catalog the
// frontend fetched once, so panels open instantly and never show an
// empty Music section while waiting on the network.
//
// Cards must ALWAYS show music. Match cascades:
//   1. exact mood + energy
//   2. same mood, any energy
//   3. "storytelling" fallback (universal vibe for narrative clips)
//   4. random sample from the whole catalog
//
// Match logic mirrors services/musicMatcher.js so client and server
// pick the same mood when given the same clip text.

const MOOD_KEYWORDS = [
  { mood: "comedic",      re: /\b(laugh(?:ing|s)?|hilarious|lmao|lol|punchline|roast(?:ed|ing|s)?|absurd|ridiculous|cracking up|joking)\b/i },
  { mood: "dark",         re: /\b(expose[ds]?|horrific|scandal(?:ous)?|destroy(?:ing|ed)?|controversial|evil|sinister|conspirac(?:y|ies)|disgusting|corrupt(?:ion)?|sick|wicked)\b/i },
  { mood: "tense",        re: /\b(piss(?:ed)?|furious|enraged|outrage(?:d)?|attack(?:ing|ed)?|confront(?:ation|ed)?|argument|argue|fight(?:ing)?|aggressive|hostile)\b/i },
  { mood: "epic",         re: /\b(incredible|legendary|insane|unbelievable|mind ?blowing|jaw ?dropping|game ?chang(?:er|ing)|historic)\b/i },
  { mood: "emotional",    re: /\b(heart(?: ?break(?:ing)?)?|tears?|crying|grief|grieving|miss(?:ed)? (?:her|him|them|you)|loss|lost (?:my|her|him)|vulnerable|emotional(?:ly)?)\b/i },
  { mood: "uplifting",    re: /\b(success(?:ful)?|thriv(?:e|ing)|inspir(?:ed|ing)|breakthrough|grateful|blessed|won (?:against|the))\b/i },
  { mood: "hopeful",      re: /\b(hope(?:ful)?|believ(?:e|ing)|healing|healed|recover(?:y|ing|ed)|rebuild(?:ing)?|start(?:ing)? over|new (?:beginning|chapter|day)|second chance|possibilit(?:y|ies)|brighter|dawn|horizon|keep going|carry on|growth|growing)\b/i },
  { mood: "love",         re: /\b(love(?:d|s|r|rs)?|loving|in love|romantic|romance|crush|soulmate|wedding|married|husband|wife|fiancée?|girlfriend|boyfriend|forever (?:with|together)|first kiss|made for (?:me|you|us|each other))\b/i },
  { mood: "sexy",         re: /\b(seduct(?:ive|ion)|sensual|sexy|sultry|flirt(?:y|ing)?|tempt(?:ation|ing)|desire|intimate|provocative|hot (?:girl|guy|night))\b/i },
  { mood: "motivating",   re: /\b(grind(?:ing)?|hustle|hustling|discipline|driven|relentless|work ?ethic|no excuses|earn(?:ed)? it|comeback|underdog|prove (?:them|him|her) wrong|unstoppable|warrior|champion|level up|locked in|sigma|alpha|grit|outwork)\b/i },
  { mood: "mystery",      re: /\b(conspirac(?:y|ies|theor(?:y|ies))|cover ?up|deep state|illuminat[ie]|hidden agenda|secret society|investigat(?:e|ed|ing|ion|or)|detective|whistleblow(?:er|ing)|leak(?:ed)?|classified|redacted|suspicious|mysterious|sinister plot|undercover|behind the scenes|whats really going on)\b/i },
  { mood: "scary",        re: /\b(scary|terrifying|horrif(?:y|ying|ic)|nightmare|haunt(?:ed|ing)|creepy|spook(?:y|ed)?|petrified|chilling|blood ?curdling|paranormal|demonic|possessed|exorcism|ghost(?:ly)?|cursed)\b/i },
  { mood: "dramatic",     re: /\b(turning point|never forget(?:ting)?|life ?chang(?:ed|ing)|biggest (?:secret|reveal|mistake)|the truth (?:is|about|behind))\b/i },
  { mood: "chill",        re: /\b(peaceful|relax(?:ing|ed)?|meditat(?:e|ing)|tranquil|easy ?going|laid ?back)\b/i },
];

const HOOK_TYPE_MOOD = {
  controversial: "tense",
  candid:        "emotional",
  reaction:      "comedic",
};

const HIGH_ENERGY = /\b(yell(?:ing)?|scream(?:ing)?|crazy|insane|explod(?:e|ing)|destroy(?:ing)?|attack(?:ing|ed)?|hype|chaos|frantic)\b/i;
const LOW_ENERGY  = /\b(whisper|gentle|soft|peaceful|reflect(?:ive)?|contemplat(?:ive|ing)|melanchol(?:y|ic)|somber|grief)\b/i;

function clipText(clip) {
  if (!clip) return "";
  // `caption` carries the worker-generated viral caption which is loaded
  // with mood-keywords ("ROMANTIC", "SHOCKING", "EMOTIONAL", etc.). It's
  // the strongest single signal we have for mood inference, so include
  // it alongside the transcript fields.
  return [clip.caption, clip.hook, clip.hookLine, clip.retentionLine, clip.payoffLine, clip.transcript]
    .filter(Boolean).join(" ");
}

export function inferMood(clip) {
  if (clip?.musicMood) return clip.musicMood;
  const text = clipText(clip);
  if (text) {
    for (const { mood, re } of MOOD_KEYWORDS) {
      if (re.test(text)) return mood;
    }
  }
  if (clip?.hookType && HOOK_TYPE_MOOD[clip.hookType]) return HOOK_TYPE_MOOD[clip.hookType];
  return null;
}

export function inferEnergy(clip) {
  if (clip?.musicEnergy) return clip.musicEnergy;
  const text = clipText(clip);
  if (HIGH_ENERGY.test(text)) return "high";
  if (LOW_ENERGY.test(text))  return "low";
  return "medium";
}

function sample(arr, n) {
  if (arr.length <= n) return [...arr];
  const copy = [...arr];
  const out = [];
  while (out.length < n && copy.length) {
    out.push(copy.splice(Math.floor(Math.random() * copy.length), 1)[0]);
  }
  return out;
}

// Lowercase + trim every instrument string in a list (handles
// "Trap Hats" vs "trap hats" without forcing case discipline on the
// admin UI or LLM output).
function normalizeInstruments(list) {
  if (!Array.isArray(list)) return [];
  return list.map((s) => String(s || "").toLowerCase().trim()).filter(Boolean);
}

// Distance between a clip's musicTarget and a single track's audio
// features. Lower = better fit. Returns +Infinity if the track hits
// an avoidInstrument (hard reject).
function audioFeatureDistance(target, track) {
  const trackInstruments = normalizeInstruments(track.instruments);
  const avoid = normalizeInstruments(target.avoidInstruments);
  // Hard reject if any track instrument matches an avoid term, either
  // exact-equal or substring (so "trap hats" avoids matches "hats").
  for (const a of avoid) {
    if (trackInstruments.some((ti) => ti === a || ti.includes(a) || a.includes(ti))) {
      return Infinity;
    }
  }

  let dist = 0;
  let dims = 0;

  // Tempo distance, normalized over 40–200 BPM range.
  if (typeof track.tempo === "number" && typeof target.tempo === "number") {
    dist += Math.abs(track.tempo - target.tempo) / 160;
    dims++;
  }
  // Valence distance, 0–1 scale.
  if (typeof track.valence === "number" && typeof target.valence === "number") {
    dist += Math.abs(track.valence - target.valence);
    dims++;
  }
  // Intensity distance, 1–5 scale.
  if (typeof track.intensity === "number" && typeof target.intensity === "number") {
    dist += Math.abs(track.intensity - target.intensity) / 4;
    dims++;
  }

  // Bonus for matching preferInstruments — pulls the track closer.
  const prefer = normalizeInstruments(target.preferInstruments);
  for (const p of prefer) {
    if (trackInstruments.some((ti) => ti === p || ti.includes(p) || p.includes(ti))) {
      dist -= 0.15;
      break; // one bonus per track, don't compound
    }
  }

  return Math.max(dist, 0);
}

// Returns { tracks, mood, energy, source } where source ∈
//   "target"   — ranked by clip.musicTarget audio-feature distance
//   "no-spec"  — clip lacks musicTarget (worker hasn't tagged it)
//   "no-data"  — catalog has zero tracks with audio features
//   "no-pool"  — no catalog tracks match the clip's mood
//   "empty"    — catalog itself is empty
//
// NO FALLBACK to the old mood-cascade is intentional. If the new
// matcher can't run, the panel shows nothing — that's the signal
// the user wants to know the new system actually fired vs not.
export function pickTracksForClip(catalog, clip, limit = 5) {
  if (!Array.isArray(catalog) || catalog.length === 0) {
    return { tracks: [], mood: null, energy: "medium", source: "empty" };
  }

  const target = clip?.musicTarget;
  if (!target) {
    return { tracks: [], mood: null, energy: "medium", source: "no-spec" };
  }

  const mood   = inferMood(clip);
  const energy = inferEnergy(clip);
  const hasMood = (t, m) => Array.isArray(t.moods) && t.moods.includes(m);
  const hasFeatures = (t) => (
    typeof t.tempo === "number" ||
    typeof t.valence === "number" ||
    typeof t.intensity === "number"
  );

  // Pre-filter by mood when present, otherwise the whole catalog.
  const moodPool = mood ? catalog.filter((t) => hasMood(t, mood)) : catalog;
  if (moodPool.length === 0) {
    return { tracks: [], mood, energy, source: "no-pool" };
  }

  // Strict: only score tracks that have AT LEAST ONE audio feature.
  // No "neutral 0.6 score" cushion for unbackfilled tracks — they
  // shouldn't show up when the matcher is running its real path.
  const scorable = moodPool.filter(hasFeatures);
  if (scorable.length === 0) {
    return { tracks: [], mood, energy, source: "no-data" };
  }

  const scored = scorable
    .map((t) => ({ track: t, d: audioFeatureDistance(target, t) }))
    .filter((s) => Number.isFinite(s.d))
    .sort((a, b) => a.d - b.d);

  if (scored.length === 0) {
    return { tracks: [], mood, energy, source: "no-data" };
  }

  return {
    tracks: scored.slice(0, limit).map((s) => s.track),
    mood,
    energy,
    source: "target",
  };
}

// Strip modifier paren-groups out of a trackName so the UI can show
// the title clean and surface modifiers ("INSTRUMENTAL", "SLOWED +
// REVERB") as separate chip badges. Shared by both Coach + Streamer
// panels so version-info displays consistently.
const MODIFIER_KEYWORDS = /(instrumental|slowed|sped[\s-]?up|reverb|remix|piano version|piano cover|acoustic|live|looped?|special version|tiktok|cover)/i;
export function parseTrackTitle(name) {
  if (!name) return { displayName: "", modifiers: [] };
  const modifiers = [];
  const cleaned = name.replace(/\s*[([]\s*([^()[\]]+?)\s*[)\]]/g, (match, inner) => {
    if (MODIFIER_KEYWORDS.test(inner)) {
      modifiers.push(inner.trim());
      return "";
    }
    return match;
  }).trim();
  return { displayName: cleaned || name, modifiers };
}

// Convenience for the legacy track shape the panel renders.
export function toUiTracks(tracks) {
  return tracks.map((t) => ({
    trackId:        t._id,
    trackName:      t.trackName,
    artist:         t.artist,
    tiktokSoundUrl: t.tiktokSoundUrl,
    spotifyUrl:     t.spotifyUrl,
  }));
}
